/**
 * Pure logic helpers for the Gutachten → Werkstatt-Akte creation flow (Issue #588).
 *
 * Extracted into a pure module so prefill-mapping, mode derivation, and insert
 * building can be covered by Vitest without a DOM or React environment.
 */

/** Field hint texts from Issue #566 — shown as tooltips in WerkstattAnlegenModal. */
export const FIELD_HINTS = {
  kunde:
    'Übernommen aus Gutachten-Akte. Bei Änderung beachten: muss auch im Gutachten korrigiert werden.',
  fahrzeug:
    'Übernommen aus Gutachten-Akte. Stamm-Daten kommen aus Schadenkalkulation.',
  kennzeichenFin:
    'Fahrzeug-Identifikationsdaten aus der Schadensaufnahme übernommen.',
  schadennummer:
    'Bei Auftrag durch Versicherung — diese Nummer wird in Rechnung verwendet.',
  versicherungGegnerisch:
    'Gegnerische Versicherung aus der Gutachten-Akte übernommen. Bei Direktauftrag ggf. anpassen.',
  unfalldatum:
    'Unfalldatum aus der Gutachten-Akte. Relevant für Fristen und Abrechnungszeitraum.',
} as const;

export interface WerkstattPrefill {
  gutachtenAkteId: string;
  gutachtenAktenNummer: string | null;
  kundeName: string | null;
  fahrzeugMarke: string | null;
  fahrzeugModell: string | null;
  kennzeichen: string | null;
  fin: string | null;
  schadennummer: string | null;
  versicherungGegnerisch: string | null;
  unfalldatum: string | null;
}

interface AkteSource {
  id: string;
  akten_nummer?: string | null;
  akten_nr?: string | null;
  anspruchsteller_name?: string | null;
  fahrzeug_marke?: string | null;
  fahrzeug_modell?: string | null;
  kennzeichen?: string | null;
  fin?: string | null;
  schadennummer?: string | null;
  versicherung?: string | null;
  unfalldatum?: string | null;
  schadendatum?: string | null;
}

/** Maps a Gutachten-Akte to form prefill values for WerkstattAnlegenModal. */
export function buildWerkstattPrefill(akte: AkteSource): WerkstattPrefill {
  return {
    gutachtenAkteId: akte.id,
    gutachtenAktenNummer: akte.akten_nummer ?? akte.akten_nr ?? null,
    kundeName: akte.anspruchsteller_name ?? null,
    fahrzeugMarke: akte.fahrzeug_marke ?? null,
    fahrzeugModell: akte.fahrzeug_modell ?? null,
    kennzeichen: akte.kennzeichen ?? null,
    fin: akte.fin ?? null,
    schadennummer: akte.schadennummer ?? null,
    versicherungGegnerisch: akte.versicherung ?? null,
    unfalldatum: akte.unfalldatum ?? akte.schadendatum ?? null,
  };
}

export type WerkstattCrossLinkMode = 'linked' | 'show_cta' | 'hidden';

/**
 * Determines what the Werkstatt section should show in the Gutachten header.
 *
 *   linked    — werkstatt_akte_id is set: show linked chip
 *   show_cta  — demontage=true, no link: show orange CTA button
 *   hidden    — demontage=false, no link: show nothing
 */
export function deriveWerkstattCrossLinkMode(
  demontageErforderlich: boolean | null | undefined,
  werkstattAkteId: string | null | undefined,
): WerkstattCrossLinkMode {
  if (werkstattAkteId) return 'linked';
  if (demontageErforderlich) return 'show_cta';
  return 'hidden';
}

export interface WerkstattAkteInsert {
  business_unit: 'werkstatt';
  werkstatt_status: 'angenommen';
  gutachten_akte_id: string;
  kunde_name: string | null;
  fahrzeug_marke: string | null;
  fahrzeug_modell: string | null;
  kennzeichen: string | null;
  fin: string | null;
  schadennummer: string | null;
  versicherung: string | null;
  unfalldatum: string | null;
  notizen: string;
}

/** Builds the Supabase insert payload for a new Werkstatt-Akte. */
export function buildWerkstattAkteInsert(prefill: WerkstattPrefill): WerkstattAkteInsert {
  const aktenNr = prefill.gutachtenAktenNummer ?? prefill.gutachtenAkteId;
  return {
    business_unit: 'werkstatt',
    werkstatt_status: 'angenommen',
    gutachten_akte_id: prefill.gutachtenAkteId,
    kunde_name: prefill.kundeName,
    fahrzeug_marke: prefill.fahrzeugMarke,
    fahrzeug_modell: prefill.fahrzeugModell,
    kennzeichen: prefill.kennzeichen,
    fin: prefill.fin,
    schadennummer: prefill.schadennummer,
    versicherung: prefill.versicherungGegnerisch,
    unfalldatum: prefill.unfalldatum,
    notizen: `Erstellt aus Gutachten ${aktenNr} am ${new Date().toISOString()}.`,
  };
}
